// @ts-nocheck
/**
 * Course Materials Mobile Module
 * Handles mobile campus/term drawers, tag sync, and student-ID flow.
 */

function getCanonicalCampusSelect() {
  if (typeof window.cmrpQueryDesktopCampusTerm === 'function') {
    return window.cmrpQueryDesktopCampusTerm('campus');
  }
  return document.querySelector('#campus-term-dropdowns #campus');
}
function getCanonicalTermSelect() {
  if (typeof window.cmrpQueryDesktopCampusTerm === 'function') {
    return window.cmrpQueryDesktopCampusTerm('term');
  }
  return document.querySelector('#campus-term-dropdowns #term');
}

function cmrpStudentIdLabel() {
  if (typeof window.getCmrpStudentIdLabel === 'function') {
    return window.getCmrpStudentIdLabel();
  }
  const s = window.shopifySettings?.cmrpFlowSwitcherStudentPlaceholder;
  const t = s != null ? String(s).trim() : '';
  return t || 'Student ID';
}

function cmrpReadFlowForMobile() {
  if (typeof window.cmrpCurrentFlow === 'function') {
    return window.cmrpCurrentFlow();
  }
  try {
    const fromStorage = String(sessionStorage.getItem('cmrp_flow') || '').trim().toLowerCase();
    if (fromStorage) return fromStorage;
  } catch (e) {
    /* ignore */
  }
  return String(new URLSearchParams(window.location.search).get('cmrp_flow') || '')
    .trim()
    .toLowerCase();
}

function cmrpPersistFlowForMobile(flow) {
  if (typeof window.cmrpPersistFlow === 'function') {
    window.cmrpPersistFlow(flow);
    return;
  }
  try {
    const normalized = String(flow || '').trim().toLowerCase();
    if (!normalized) {
      sessionStorage.removeItem('cmrp_flow');
      return;
    }
    sessionStorage.setItem('cmrp_flow', normalized);
  } catch (e) {
    /* ignore */
  }
}

class CourseMaterialsMobile {
  constructor(config = {}) {
    this.config = {
      ddcs_campus_entity: config.ddcs_campus_entity || [],
      ddcs_campus_division: config.ddcs_campus_division || [],
      ddcs_term: config.ddcs_term || [],
      ddcs_campus_department: config.ddcs_campus_department || [],
      ddcs_course: config.ddcs_course || [],
      ddcs_section: config.ddcs_section || [],
      container: config.container || document,
      ...config,
    };

    this.currentMode = 'course'; // 'course' or 'studentid'

    // Cache URL params immediately before any other code can modify them
    const urlParams = new URLSearchParams(window.location.search);
    this.cachedUrlParams = {
      shopBy: urlParams.get('shopBy'),
      cmrpFlow: cmrpReadFlowForMobile(),
      campusId: urlParams.get('campusId'),
      termId: urlParams.get('termId'),
    };

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }
  /**
   * ADD/EDIT COURSES trigger: disabled in HTML until init runs (DOM + deferred scripts ready).
   */
  enableMobileCourseButtons() {
    document.querySelectorAll('.mobile-course-btn').forEach(btn => {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
    });
  }

  async checkUrlParameters(session_check) {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const hasSectionDisplayName = !!params.get('sectionDisplayName');
    const hasCourseSelections =
      hasSectionDisplayName || params.get('courseDisplayName');

    if (hasCourseSelections) {
      this.scheduleDrawerClose();
    }
    void session_check;
  }

  /**
   * Schedule drawer close after results have loaded
   * Used when URL has course selection parameters
   */
  scheduleDrawerClose() {
    /* Mobile results drawer removed — nothing to close */
  }
  /**
   * Apply student-ID drawer mode from URL before session check so the drawer
   * does not briefly show shop-by-course when shopBy=studentid or cmrp_flow=student-id.
   */
  applyDrawerModeFromCachedUrl() {
    if (!window.shopifySettings?.studentIDActivation) return;
    const { shopBy, cmrpFlow } = this.cachedUrlParams || {};
    if (shopBy === 'studentid' || cmrpFlow === 'student-id') {
      this.setMode('studentid', { skipFlowReset: true });
    }
  }

  async init() {
    this.enableMobileCourseButtons();

    this.bindMobileEvents();

    this.applyDrawerModeFromCachedUrl();

    const session_check = await this.checkSession();
    await this.restoreStudentIdFromURL(session_check);

    await this.checkUrlParameters(session_check);

    const campusRowsReady =
      (window.courseData?.ddcs_campus_entity?.length ?? 0) > 0;
    const runCampusRestore = () => this.restoreCampusAndTermFromURL();
    if (campusRowsReady) {
      runCampusRestore();
    } else {
      document.addEventListener(
        'cmrp:campusTermDataReady',
        () => runCampusRestore(),
        { once: true }
      );
    }

    this.initializeSelectionDrawers();
    this.bindCourseRemovedEvent();
    // Initialize submit button state
    this.updateMobileSubmitButtonState();

    // Sync display text after a delay to catch desktop URL restoration
    // The desktop url-state-manager sets dropdowns asynchronously
    this.scheduledDisplaySync();
  }

  /**
   * Schedule a sync of display text to catch desktop URL restoration
   * The desktop url-state-manager.js restores dropdowns asynchronously via API calls,
   * so we need to poll until the values are set and sync the mobile display text
   */
  scheduledDisplaySync() {
    let attempts = 0;
    const maxAttempts = 20; // Check for up to 10 seconds (20 * 500ms)

    const checkAndSync = () => {
      attempts++;
      const synced = this.syncDisplayWithSelectValues();

      // Keep checking until both are synced or max attempts reached
      if (!synced && attempts < maxAttempts) {
        setTimeout(checkAndSync, 500);
      }
    };

    // Start checking after a short delay
    setTimeout(checkAndSync, 100);
  }

  /**
   * Sync mobile display text with actual select values
   * Called after desktop URL restoration may have set values
   * @returns {boolean} true if both campus and term are synced
   */
  syncDisplayWithSelectValues() {
    const campusSelect = getCanonicalCampusSelect();
    const termSelect = getCanonicalTermSelect();
    const campusDisplay = document.getElementById('campus-display-text');
    const termDisplay = document.getElementById('term-display-text');

    let campusSynced = false;
    let termSynced = false;

    // Sync campus if value is set but display shows placeholder
    if (campusSelect?.value && campusDisplay) {
      const isPlaceholder = campusDisplay.getAttribute('data-is-placeholder') === 'true';
      if (isPlaceholder) {
        this.updateCampusDisplay();
      }
      campusSynced = true;
    }

    // Sync term if value is set but display shows placeholder
    if (termSelect?.value && termDisplay) {
      const isPlaceholder = termDisplay.getAttribute('data-is-placeholder') === 'true';
      if (isPlaceholder) {
        this.updateTermDisplay();
      }
      termSynced = true;
    }

    // Update button state if in student ID mode
    if (this.currentMode === 'studentid') {
      this.updateMobileSubmitButtonStateForStudentId();
    }

    return campusSynced && termSynced;
  }
  
  // Listen for course removed events from desktop
  bindCourseRemovedEvent() {
    document.addEventListener('sidebar:courseRemoved', (event) => {
      const course = event.detail?.course;
      if (course) {
        // Find and remove mobile tag
        const department = course.selectedDepartment || course.department;
        const courseCode = course.selectedcourse || course.course;
        const section = course.selectedsection || course.section;
        
        if (department && courseCode && section) {
          this.removeMobileCourseTagByValues(department, courseCode, section);
        }
      }
    });
  }
  
  // Remove mobile course tag by values (called from desktop remove)
  removeMobileCourseTagByValues(department, course, section) {
    if (!department || !course || !section) {
      return;
    }
    
    // Find all mobile tags and match by comparing values
    const allMobileTags = document.querySelectorAll('.mobile-course-tag');
    let mobileTag = null;
    
    allMobileTags.forEach(tag => {
      const tagDept = tag.getAttribute('data-department') || '';
      const tagCourse = tag.getAttribute('data-course') || '';
      const tagSection = tag.getAttribute('data-section') || '';
      
      // Normalize values for comparison (trim whitespace)
      const normalizedDept = tagDept.trim();
      const normalizedCourse = tagCourse.trim();
      const normalizedSection = tagSection.trim();
      const normalizedInputDept = String(department).trim();
      const normalizedInputCourse = String(course).trim();
      const normalizedInputSection = String(section).trim();
      
      // Match if all three values match
      if (normalizedDept === normalizedInputDept && 
          normalizedCourse === normalizedInputCourse && 
          normalizedSection === normalizedInputSection) {
        mobileTag = tag;
      }
    });
    
    if (mobileTag) {
      // Remove the tag
      mobileTag.remove();
      
      // Check if no courses left
      const selectedCoursesContainer = document.getElementById('mobile-selected-courses');
      const remainingTags = selectedCoursesContainer?.querySelectorAll('.mobile-course-tag');
      const hasNoCourses = !remainingTags || remainingTags.length === 0;
      
      if (hasNoCourses) {
        // Hide container
        if (selectedCoursesContainer) {
          selectedCoursesContainer.style.display = 'none';
        }
        
        // Handle empty state
        this.handleEmptyCoursesState();
      }
      
      // Update mobile submit button state
      this.updateMobileSubmitButtonState();
    }
  }
  
  // Initialize selection drawers
  initializeSelectionDrawers() {
    // Sync display text with select values on init
    this.updateCampusDisplay();
    this.updateTermDisplay();
    
    // Listen for changes to select elements
    const campusSelect = getCanonicalCampusSelect();
    const termSelect = getCanonicalTermSelect();
    
    if (campusSelect) {
      campusSelect.addEventListener('change', () => {
        this.updateCampusDisplay();
      });
    }
    
    if (termSelect) {
      termSelect.addEventListener('change', () => {
        this.updateTermDisplay();
      });
    }
  }
  
  // Update campus display text
  updateCampusDisplay() {
    const campusSelect = getCanonicalCampusSelect();
    const campusDisplay = document.getElementById('campus-display-text');
    
    if (campusSelect && campusDisplay) {
      const selectedOption = campusSelect.options[campusSelect.selectedIndex];
      const displayText = selectedOption.value || 'Campus';
      campusDisplay.textContent = displayText;
      
      // Set placeholder attribute for styling
      if (!selectedOption.value || selectedOption.value === '') {
        campusDisplay.setAttribute('data-is-placeholder', 'true');
      } else {
        campusDisplay.removeAttribute('data-is-placeholder');
      }
    }
  }
  
  // Update term display text
  updateTermDisplay() {
    const termSelect = getCanonicalTermSelect();
    const termDisplay = document.getElementById('term-display-text');
    
    if (termSelect && termDisplay) {
      const selectedOption = termSelect.options[termSelect.selectedIndex];
      const displayText = selectedOption.text || 'Term';
      termDisplay.textContent = displayText;
      
      // Set placeholder attribute for styling
      if (!selectedOption.value || selectedOption.value === '') {
        termDisplay.setAttribute('data-is-placeholder', 'true');
      } else {
        termDisplay.removeAttribute('data-is-placeholder');
      }
    }
  }
  
  // Open campus selection drawer
  openCampusDrawer() {
    const drawer = document.getElementById('campus-selection-drawer');
    if (!drawer) return;

    this.populateCampusOptions();

    const campusSelect = getCanonicalCampusSelect();
    const realOptions = campusSelect
      ? Array.from(campusSelect.options).slice(1)
      : [];
    if (realOptions.length === 1) {
      this.selectedCampusOption = realOptions[0].value;
      this.confirmCampusSelection();
      return;
    }

    drawer.classList.add('active');
    this.syncMobileDrawerBodyScroll();
  }
  
  // Close campus selection drawer
  closeCampusDrawer() {
    const drawer = document.getElementById('campus-selection-drawer');
    if (!drawer) return;
    
    drawer.classList.remove('active');
    this.syncMobileDrawerBodyScroll();
    
    // Reset selection state
    this.selectedCampusOption = null;
    const options = drawer.querySelectorAll('.mobile-selection-option');
    options.forEach(opt => opt.classList.remove('selected'));
    
    const nextBtn = document.getElementById('campus-next-btn');
    if (nextBtn) {
      nextBtn.disabled = true;
    }
  }
  
  // Populate campus options
  populateCampusOptions() {
    const optionsList = document.getElementById('campus-options-list');
    if (!optionsList) return;
    
    const campusSelect = getCanonicalCampusSelect();
    if (!campusSelect) return;
    
    optionsList.innerHTML = '';
    
    // Get all campus options from select (excluding the first empty option)
    const options = Array.from(campusSelect.options).slice(1);
    
    options.forEach(option => {
      const optionElement = document.createElement('div');
      optionElement.className = 'mobile-selection-option';
      optionElement.setAttribute('data-value', option.value);
      
      const isSelected = campusSelect.value === option.value;
      if (isSelected) {
        optionElement.classList.add('selected');
        this.selectedCampusOption = option.value;
      }
      
      const optLabel =
        typeof window.cmrpOptionFullLabel === 'function'
          ? window.cmrpOptionFullLabel(option)
          : option.textContent;
      optionElement.innerHTML = `
        <span class="mobile-selection-option-text">${optLabel}</span>
        <svg class="mobile-selection-option-checkmark" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      
      optionElement.addEventListener('click', () => {
        this.selectCampusOption(option.value, optionElement);
      });
      
      optionsList.appendChild(optionElement);
    });
    
    // Update next button state
    this.updateCampusNextButton();
  }
  
  // Select campus option
  selectCampusOption(value, element) {
    // Remove previous selection
    const options = document.querySelectorAll('#campus-options-list .mobile-selection-option');
    options.forEach(opt => opt.classList.remove('selected'));
    
    // Add selection to clicked item
    element.classList.add('selected');
    this.selectedCampusOption = value;
    
    // Update next button state
    this.updateCampusNextButton();
  }
  
  // Update campus next button state
  updateCampusNextButton() {
    const nextBtn = document.getElementById('campus-next-btn');
    if (nextBtn) {
      nextBtn.disabled = !this.selectedCampusOption;
    }
  }
  
  // Confirm campus selection
  confirmCampusSelection() {
    if (!this.selectedCampusOption) return;
    
    const campusSelect = getCanonicalCampusSelect();
    if (campusSelect) {
      campusSelect.value = this.selectedCampusOption;
      window.courseMaterialsSidebar?.syncMobileDrawerCampusTermSelects?.();

      // Trigger change event to populate terms
      const changeEvent = new Event('change', { bubbles: true });
      campusSelect.dispatchEvent(changeEvent);
      
      // Also call the populateTerms function directly
      if (window.courseMaterialsSidebar) {
        window.courseMaterialsSidebar.populateTerms();
      }
    }
    
    // Update display text
    this.updateCampusDisplay();
    
    // Close drawer
    this.closeCampusDrawer();
  }
  
  // Open term selection drawer
  openTermDrawer() {
    const drawer = document.getElementById('term-selection-drawer');
    if (!drawer) return;

    const campusSelect = getCanonicalCampusSelect();
    if (!campusSelect || !campusSelect.value) {
      return;
    }

    this.populateTermOptions();

    const termSelect = getCanonicalTermSelect();
    const realOptions = termSelect
      ? Array.from(termSelect.options).slice(1)
      : [];
    if (realOptions.length === 1) {
      this.selectedTermOption = realOptions[0].value;
      this.confirmTermSelection();
      return;
    }

    drawer.classList.add('active');
    this.syncMobileDrawerBodyScroll();
  }
  
  // Close term selection drawer
  closeTermDrawer() {
    const drawer = document.getElementById('term-selection-drawer');
    if (!drawer) return;
    
    drawer.classList.remove('active');
    this.syncMobileDrawerBodyScroll();
    
    // Reset selection state
    this.selectedTermOption = null;
    const options = drawer.querySelectorAll('.mobile-selection-option');
    options.forEach(opt => opt.classList.remove('selected'));
    
    const nextBtn = document.getElementById('term-next-btn');
    if (nextBtn) {
      nextBtn.disabled = true;
    }
  }
  
  // Populate term options
  populateTermOptions() {
    const optionsList = document.getElementById('term-options-list');
    if (!optionsList) return;
    
    const termSelect = getCanonicalTermSelect();
    if (!termSelect) return;
    
    optionsList.innerHTML = '';
    
    // Get all term options from select (excluding the first empty option)
    const options = Array.from(termSelect.options).slice(1);
    
    if (options.length === 0) {
      optionsList.innerHTML = '<div class="mobile-selection-option-text" style="padding: 20px; text-align: center; color: #666;">Please select a campus first.</div>';
      return;
    }
    
    options.forEach(option => {
      const optionElement = document.createElement('div');
      optionElement.className = 'mobile-selection-option';
      optionElement.setAttribute('data-value', option.value);
      
      const isSelected = termSelect.value === option.value;
      if (isSelected) {
        optionElement.classList.add('selected');
        this.selectedTermOption = option.value;
      }
      
      const optLabel =
        typeof window.cmrpOptionFullLabel === 'function'
          ? window.cmrpOptionFullLabel(option)
          : option.textContent;
      optionElement.innerHTML = `
        <span class="mobile-selection-option-text">${optLabel}</span>
        <svg class="mobile-selection-option-checkmark" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M16.6667 5L7.50004 14.1667L3.33337 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      
      optionElement.addEventListener('click', () => {
        this.selectTermOption(option.value, optionElement);
      });
      
      optionsList.appendChild(optionElement);
    });
    
    // Update next button state
    this.updateTermNextButton();
  }
  
  // Select term option
  selectTermOption(value, element) {
    // Remove previous selection
    const options = document.querySelectorAll('#term-options-list .mobile-selection-option');
    options.forEach(opt => opt.classList.remove('selected'));
    
    // Add selection to clicked item
    element.classList.add('selected');
    this.selectedTermOption = value;
    
    // Update next button state
    this.updateTermNextButton();
  }
  
  // Update term next button state
  updateTermNextButton() {
    const nextBtn = document.getElementById('term-next-btn');
    if (nextBtn) {
      nextBtn.disabled = !this.selectedTermOption;
    }
  }
  
  // Confirm term selection
  confirmTermSelection() {
    if (!this.selectedTermOption) return;
    
    const termSelect = getCanonicalTermSelect();
    if (termSelect) {
      if(termSelect.value != this.selectedTermOption){
        let selectedCoursesContainer = document.getElementById('mobile-selected-courses');
        if (selectedCoursesContainer) {
          selectedCoursesContainer.style.display = 'none';
          selectedCoursesContainer.innerHTML = '';
        }
      }
      termSelect.value = this.selectedTermOption;
      window.courseMaterialsSidebar?.syncMobileDrawerCampusTermSelects?.();

      // Trigger change event to populate departments
      const changeEvent = new Event('change', { bubbles: true });
      termSelect.dispatchEvent(changeEvent);

      // Also call the populateDepartmentsFromTerms function directly
      if (window.courseMaterialsSidebar) {
        window.courseMaterialsSidebar.populateDepartmentsFromTerms();
      }
    }
    
    // Update display text
    this.updateTermDisplay();
    
    // Close drawer
    this.closeTermDrawer();
  }

  // Set campus entity ID (like desktop)
  setCampusEntityId(campusName) {
    // Just set the campus_entity_id without triggering desktop logic
    const normalize = value =>
      String(value ?? '')
        .toLowerCase()
        .replace(/\s+/g, '');

    const filteredCampus = this.config.ddcs_campus_entity.filter(campus =>
      normalize(campus.name).includes(normalize(campusName))
    );
    
    if (filteredCampus.length > 0 && window.courseMaterialsSidebar) {
      window.courseMaterialsSidebar.campus_entity_id = filteredCampus[0].campus_entity_id;
      window.courseMaterialsSidebar.filteredCampus = filteredCampus[0];
    }
  }

  // Bind mobile-specific events
  bindMobileEvents() {
    // Submit button state management
    const submitButton = document.getElementById('mobile-submit-student-id');
    if (submitButton) {
      submitButton.addEventListener('click', () => {
        // Prevent click when button is disabled
        if (submitButton.disabled) {
          return;
        }
        this.handleMobileSubmit();
      });
    }

    // Student ID input event for real-time validation
    const studentIdInput = document.getElementById('mobile-student-id-input');
    if (studentIdInput) {
      studentIdInput.addEventListener('input', () => {
        if (this.currentMode === 'studentid') {
          this.updateMobileSubmitButtonStateForStudentId();
          // Clear error on input
          this.hideStudentIdError();
        }
      });
    }

    // Campus/Term dropdown change events - update student ID button state
    const campusSelect = getCanonicalCampusSelect();
    const termSelect = getCanonicalTermSelect();

    if (campusSelect) {
      campusSelect.addEventListener('change', () => {
        if (this.currentMode === 'studentid') {
          this.updateMobileSubmitButtonStateForStudentId();
        }
      });
    }

    if (termSelect) {
      termSelect.addEventListener('change', () => {
        if (this.currentMode === 'studentid') {
          this.updateMobileSubmitButtonStateForStudentId();
        }
      });
    }
  }

  isMainCourseMaterialsDrawerOpen() {
    return (
      document
        .getElementById('mobile-drawer-section')
        ?.classList.contains('mobile-drawer-open') === true
    );
  }

  /**
   * Keep page scroll locked while the CM mobile drawer or any sub-sheet is open.
   * overflow:hidden alone is unreliable on iOS; position:fixed + restore scrollY is used when locking.
   */
  syncMobileDrawerBodyScroll() {
    const campusOpen =
      document
        .getElementById('campus-selection-drawer')
        ?.classList.contains('active') === true;
    const termOpen =
      document
        .getElementById('term-selection-drawer')
        ?.classList.contains('active') === true;

    const shouldLock =
      this.isMainCourseMaterialsDrawerOpen() ||
      campusOpen ||
      termOpen;

    if (shouldLock) {
      if (!this._cmrpScrollLockActive) {
        this._cmrpSavedScrollY =
          window.scrollY ||
          document.documentElement.scrollTop ||
          document.body.scrollTop ||
          0;
        document.documentElement.classList.add('cmrp-scroll-lock');
        document.body.classList.add('cmrp-scroll-lock');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this._cmrpSavedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        this._cmrpScrollLockActive = true;
      }
    } else if (this._cmrpScrollLockActive) {
      document.documentElement.classList.remove('cmrp-scroll-lock');
      document.body.classList.remove('cmrp-scroll-lock');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      const y = this._cmrpSavedScrollY || 0;
      this._cmrpScrollLockActive = false;
      this._cmrpSavedScrollY = 0;
      window.requestAnimationFrame(() => {
        window.scrollTo(0, y);
      });
    }
  }

  // Create course tag
  createCourseTag(courseData) {
    const tag = document.createElement('div');
    tag.className = 'mobile-course-tag';

    // Same key as desktop .course-chip / sidebar (division|department|course|section)
    const courseKey =
      typeof window.CourseMaterialsSidebar?.courseSelectionKey === 'function'
        ? window.CourseMaterialsSidebar.courseSelectionKey(
            courseData.department,
            courseData.course,
            courseData.section,
            courseData.division || '',
          )
        : `${courseData.division || ''}|${courseData.department}|${courseData.course}|${courseData.section}`;

    const text = document.createElement('span');
    text.className = 'mobile-course-tag-text';
    text.textContent = courseData.division
      ? `${courseData.division} | ${courseData.department} ${courseData.course} | ${courseData.section}`
      : `${courseData.department} ${courseData.course} | ${courseData.section}`;

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'mobile-course-tag-remove';
    removeBtn.setAttribute('aria-label', 'Remove course');
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      this.removeCourseTag(removeBtn);
    });

    tag.addEventListener('click', e => {
      if (e.target instanceof Element && e.target.closest('.mobile-course-tag-remove')) {
        return;
      }
      this.removeCourseTag(tag);
    });

    tag.appendChild(text);
    tag.appendChild(removeBtn);

    tag.setAttribute('data-course-key', courseKey);
    tag.setAttribute('data-division', courseData.division || '');
    tag.setAttribute('data-department', courseData.department);
    tag.setAttribute('data-course', courseData.course);
    tag.setAttribute('data-section', courseData.section);
    tag.setAttribute('data-course-builder', 'true');

    return tag;
  }

  // Add course tag to mobile drawer
  addCourseTagToMobileDrawer(tag) {
    let selectedCoursesContainer = document.getElementById('mobile-selected-courses');
    if (!selectedCoursesContainer) {
      // Create container if it doesn't exist
      selectedCoursesContainer = document.createElement('div');
      selectedCoursesContainer.id = 'mobile-selected-courses';
      selectedCoursesContainer.className = 'mobile-selected-courses';
      
      // Insert after find courses input
      const findCoursesSection = document.querySelector('.mobile-find-courses-section');
      if (findCoursesSection) {
        findCoursesSection.appendChild(selectedCoursesContainer);
      }
    }
    
    selectedCoursesContainer.appendChild(tag);
    selectedCoursesContainer.style.display = 'flex';
    
    // Remove empty state if present
    const drawerContent = document.querySelector('.mobile-drawer-content');
    if (drawerContent) {
      drawerContent.classList.remove('empty-state');
    }
    
    // Don't hide initial placeholder here - wait for SUBMIT
    // Initial placeholder will be hidden when SUBMIT is clicked
    
    // Update button state
    this.updateMobileSubmitButtonState();
  }

  /**
   * Populate #mobile-selected-courses from the same filter list as desktop chips (URL restore / refresh).
   * Only shows courses added via the manual course-builder flow — never student-ID discovery rows.
   * @param {Array<Record<string, unknown>>} filters
   */
  syncMobileTagsFromFilters(filters) {
    const container = document.getElementById('mobile-selected-courses');
    if (!container) return;

    // Empty filters: wipe tags (used by student-ID / cross-flow clears). Do not early-return
    // without clearing — syncBubblesFromFilters([]) relied on this and left stale course-builder tags.
    if (!filters || !Array.isArray(filters) || filters.length === 0) {
      container.innerHTML = '';
      container.style.display = 'none';
      this.handleEmptyCoursesState();
      this.updateMobileSubmitButtonState();
      return;
    }

    if (this.currentMode !== 'course') return;

    const pick = f => {
      if (!f) return null;
      const department = f.selectedDepartment ?? f.selecteddepartment ?? '';
      const course = f.selectedcourse ?? f.course ?? '';
      const section = f.selectedsection ?? f.section ?? '';
      const division = f.selectedDivision ?? f.selecteddivision ?? '';
      const d = String(department).trim();
      const c = String(course).trim();
      const s = String(section).trim();
      if (!d || !c || !s) return null;
      return {
        department: d,
        course: c,
        section: s,
        division: String(division || '').trim(),
      };
    };

    // Remove any tags that correspond to student-ID-origin filters (or legacy wrongly-added tags).
    filters.forEach(filter => {
      if (!filter || !filter.fromStudentId) return;
      const row = pick(filter);
      if (!row) return;
      const courseKey =
        typeof window.CourseMaterialsSidebar?.courseSelectionKey === 'function'
          ? window.CourseMaterialsSidebar.courseSelectionKey(
              row.department,
              row.course,
              row.section,
              row.division,
            )
          : `${row.division}|${row.department}|${row.course}|${row.section}`;
      container.querySelectorAll('.mobile-course-tag').forEach(el => {
        if (el.getAttribute('data-course-key') === courseKey) {
          el.remove();
        }
      });
    });

    const existingKeys = new Set(
      Array.from(container.querySelectorAll('.mobile-course-tag')).map(el =>
        el.getAttribute('data-course-key')
      )
    );

    filters.forEach(filter => {
      if (!filter || filter.fromStudentId) return;
      const row = pick(filter);
      if (!row) return;
      const courseKey =
        typeof window.CourseMaterialsSidebar?.courseSelectionKey === 'function'
          ? window.CourseMaterialsSidebar.courseSelectionKey(
              row.department,
              row.course,
              row.section,
              row.division,
            )
          : `${row.division}|${row.department}|${row.course}|${row.section}`;
      if (existingKeys.has(courseKey)) return;

      const tag = this.createCourseTag({
        division: row.division,
        department: row.department,
        course: row.course,
        section: row.section,
        courseName: row.course,
      });
      container.appendChild(tag);
      existingKeys.add(courseKey);
    });

    if (container.querySelector('.mobile-course-tag')) {
      container.style.display = 'flex';
      document.querySelector('.mobile-drawer-content')?.classList.remove('empty-state');
      this.updateMobileSubmitButtonState();
    } else {
      container.style.display = 'none';
      this.handleEmptyCoursesState();
      this.updateMobileSubmitButtonState();
    }
  }

  /**
   * When opening the drawer or after navigation, backfill mobile tags if page has filters but tags are missing.
   */
  ensureMobileTagsMatchPageSelection() {
    if (this.currentMode !== 'course') return;
    const page = window.courseMaterialsPageInstance;
    const filters = page?.selectedFilters;
    if (!filters || filters.length === 0) return;
    const container = document.getElementById('mobile-selected-courses');
    if (!container) return;
    const manualCount = filters.filter(f => f && !f.fromStudentId).length;
    const n = container.querySelectorAll('.mobile-course-tag').length;
    const hasStudentIdFilters = filters.some(f => f && f.fromStudentId);
    if (n >= manualCount && !hasStudentIdFilters) return;
    this.syncMobileTagsFromFilters(filters);
  }

  // Remove course tag (pass remove control or the tag / any element inside the tag)
  removeCourseTag(button) {
    const tag = button?.classList?.contains('mobile-course-tag')
      ? button
      : button?.closest('.mobile-course-tag');
    if (!tag) return;

    const department = tag.getAttribute('data-department');
    const course = tag.getAttribute('data-course');
    const section = tag.getAttribute('data-section');
    const division = tag.getAttribute('data-division') || '';

    const cmPage =
      window.courseMaterialsPage || window.courseMaterialsPageInstance;
    cmPage?.removeCourseSelection?.(department, course, section, division);
  }
  
  /**
   * True when the page already has course selections or product results (e.g. after submit).
   */
  hasActiveCourseSelectionOrResults() {
    try {
      const page = window.courseMaterialsPageInstance;
      if (page?.selectedFilters?.length > 0) return true;
    } catch {
      /* ignore */
    }
    if (document.getElementById('course-list')?.querySelector('.course-item')) {
      return true;
    }
    const filtered = document.getElementById('filtered-products');
    if (filtered?.children?.length > 0) return true;
    return false;
  }

  // Handle empty courses state
  handleEmptyCoursesState() {
    if (this.hasActiveCourseSelectionOrResults()) {
      const drawerContent = document.querySelector('.mobile-drawer-content');
      drawerContent?.classList.remove('empty-state');
      return;
    }

    const drawerContent = document.querySelector('.mobile-drawer-content');
    
    if (!drawerContent) return;
    
    // Add empty state class to disable scrolling
    drawerContent.classList.add('empty-state');
    
    // Hide mobile-selected-courses container
    const selectedCoursesContainer = document.getElementById('mobile-selected-courses');
    if (selectedCoursesContainer) {
      selectedCoursesContainer.style.display = 'none';
    }
    
    // Show Initial Placeholder State in right panel
    this.showInitialPlaceholder();
  }
  
  // Show Initial Placeholder State
  showInitialPlaceholder() {
    if (this.hasActiveCourseSelectionOrResults()) {
      return;
    }

    const filteredProducts = document.getElementById('filtered-products');
    const noProductsFound = document.getElementById('no-products-found');
    const rightPanelNav = document.getElementById('right-panel-nav');
    
    // Hide filtered products
    if (filteredProducts) {
      filteredProducts.style.display = 'none';
    }
    
    // Hide no products found message
    if (noProductsFound) {
      noProductsFound.style.display = 'none';
    }
    
    // Hide right panel nav
    if (rightPanelNav) {
      rightPanelNav.style.display = 'none';
    }
  }
  
  // Hide Initial Placeholder State (when courses are added)
  hideInitialPlaceholder() {
    const initialPlaceholder = document.getElementById('initial-placeholder');
    if (initialPlaceholder) {
      initialPlaceholder.style.display = 'none';
    }

    const filteredProducts = document.getElementById('filtered-products');
    if (filteredProducts?.children?.length > 0) {
      filteredProducts.style.removeProperty('display');
    }
  }

  // Update mobile submit button state
  updateMobileSubmitButtonState() {
    // If in student ID mode, use the student ID-specific method
    if (this.currentMode === 'studentid') {
      this.updateMobileSubmitButtonStateForStudentId();
      return;
    }

    const submitButton = document.getElementById('mobile-submit-student-id');
    const selectedCourses = document.querySelectorAll('.mobile-course-tag');

    if (submitButton) {
      if (selectedCourses.length > 0) {
        submitButton.disabled = false;
        submitButton.classList.add('active');
      } else {
        submitButton.disabled = true;
        submitButton.classList.remove('active');
      }
    }
  }

  // Create course object (like desktop)
  createCourseObject(courseData) {
    const campusSelect = getCanonicalCampusSelect();
    const termSelect = getCanonicalTermSelect();
    
    // Get term ID and name for selectedTermId
    let selectedTermId = '';
    let selectedTermName = '';
    const termValue = termSelect?.value || '';
    if (termValue && window.courseData?.ddcs_term) {
      const normalize = value =>
        String(value).toLowerCase().replace(/\s+/g, '');
      const filteredTerm = window.courseData.ddcs_term.filter(term =>
        normalize(term.term_id) === normalize(termValue) || normalize(term.name) === normalize(termValue)
      );
      if (filteredTerm.length > 0) {
        selectedTermId = filteredTerm[0].term_id;
        selectedTermName = filteredTerm[0].name || termValue;
      } else {
        selectedTermName = termValue;
      }
    }
    
    const campusValue = campusSelect?.value || '';
    
    return {
      division: courseData.division || '',
      department: courseData.department || '',
      course: courseData.course || '',
      section: courseData.section || '',
      selectedCampus: campusValue,
      selectedTerm: selectedTermName,
      selectedTermId: selectedTermId,
      selectedDivision: courseData.division || '',
      selectedDepartment: courseData.department || '',
      selectedcourse: courseData.course || '',
      selectedsection: courseData.section || '',
      fromStudentId: false, // Mark as not from Student ID search
      key: `${courseData.division || ''}-${courseData.department || ''}-${courseData.course || ''}-${courseData.section || ''}`
    };
  }

  // Close mobile drawer
  closeMobileDrawer() {
    if (window.courseMaterialsSidebar) {
      window.courseMaterialsSidebar.closeMobileDrawer();
    }
  }

  // ==========================================================
  // STUDENT ID MODE METHODS
  // ==========================================================

  async checkSession() {
    if (typeof window.cmrpGlobalCheckSession === 'function') {
      return window.cmrpGlobalCheckSession();
    }
    const token = sessionStorage.getItem('sessionToken');
    if (!token) return false;

    try {
      const response = await fetch('/apps/shopifyData/session/check', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);

      return await response.json();
    } catch (error) {
      console.error('Error checking session:', error);
    }
  }

  /**
   * Populate student ID input and student mode from session (single session fetch; campus/term
   * restore runs after cmrp:campusTermDataReady when lists are loaded).
   */
  async restoreStudentIdFromURL(session_check) {
    if (!window.shopifySettings?.studentIDActivation) return;

    const sc =
      session_check !== undefined ? session_check : await this.checkSession();
    let studentId = false;
    if (sc && sc.status) {
      studentId = sc.sessionID;
    }
    if (!studentId) {
      try {
        const v = sessionStorage.getItem('cmrp_student_id_prefill');
        if (v) studentId = String(v).trim();
      } catch (e) {
        /* ignore */
      }
    }
    if (!studentId) {
      const mobileEl = document.getElementById('mobile-student-id-input');
      const desktopEl = document.getElementById('student-id');
      const fromInputs = (mobileEl?.value || desktopEl?.value || '').trim();
      if (fromInputs) studentId = fromInputs;
    }

    if (!studentId) return;

    const studentIdInput = document.getElementById('mobile-student-id-input');
    if (studentIdInput) {
      studentIdInput.value = studentId;
    }
    this.setMode('studentid', { skipFlowReset: true });
  }

  /**
   * Restore campus and term from URL parameters
   * Maps campusId and termId entity IDs to dropdown values
   */
  restoreCampusAndTermFromURL() {
    const { campusId, termId } = this.cachedUrlParams || {};

    if (!campusId) return;

    const campusSelect = getCanonicalCampusSelect();
    const termSelect = getCanonicalTermSelect();

    if (!campusSelect) return;

    // Find campus by entity ID from courseData (case-insensitive comparison)
    const campusData = window.courseData?.ddcs_campus_entity || this.config.ddcs_campus_entity || [];
    const campusIdLower = campusId.toLowerCase();
    const matchingCampus = campusData.find(campus =>
      campus.campus_entity_id?.toLowerCase() === campusIdLower
    );

    if (matchingCampus) {
      // Set campus dropdown value (uses campus name)
      campusSelect.value = matchingCampus.name;

      // Update display text for mobile
      this.updateCampusDisplay();

      // Trigger campus change to populate terms
      const changeEvent = new Event('change', { bubbles: true });
      campusSelect.dispatchEvent(changeEvent);

      // Also call populateTerms directly to ensure terms are populated
      if (window.courseMaterialsSidebar) {
        window.courseMaterialsSidebar.populateTerms();
      }

      // Set term after a delay to allow terms to populate
      if (termId && termSelect) {
        setTimeout(() => {
          this.setTermFromURLParam(termId);
        }, 100);
      }
    }
  }

  /**
   * Set term dropdown from URL parameter after terms are populated
   * @param {string} termId - The term entity ID from URL
   */
  setTermFromURLParam(termId) {
    const termSelect = getCanonicalTermSelect();
    if (!termSelect) return;

    // Term dropdown uses term_id as value (case-insensitive comparison)
    const termIdLower = termId.toLowerCase();
    const termOption = Array.from(termSelect.options).find(option =>
      option.value?.toLowerCase() === termIdLower
    );

    if (termOption) {
      // Use the actual option value (may have different casing than URL param)
      termSelect.value = termOption.value;

      // Update display text for mobile
      this.updateTermDisplay();

      // Trigger change event to populate departments
      const changeEvent = new Event('change', { bubbles: true });
      termSelect.dispatchEvent(changeEvent);

      // Also call populateDepartmentsFromTerms directly
      if (window.courseMaterialsSidebar) {
        window.courseMaterialsSidebar.term_id = termOption.value;
        window.courseMaterialsSidebar.populateDepartmentsFromTerms();
      }

      // Update submit button state
      this.updateMobileSubmitButtonStateForStudentId();
    }
  }

  /**
   * Full wipe when the user switches course vs student-ID tabs in the mobile drawer:
   * selections, bubbles/tags, product grid, sidebar list, URL course params, and drafts.
   * (URL/session-driven `setMode` passes `{ skipFlowReset: true }` so init/restore does not clear.)
   */
  resetPriorFlowStateOnMobileTabSwitch() {
    try {
      sessionStorage.removeItem('cmrp_student_id_prefill');
    } catch (e) {
      /* ignore */
    }
    const studentDesktop = document.getElementById('student-id');
    const studentMobile = document.getElementById('mobile-student-id-input');
    if (studentDesktop) studentDesktop.value = '';
    if (studentMobile) studentMobile.value = '';

    const page = window.courseMaterialsPageInstance;
    if (page) {
      page.components.selectionManager?.clearSelections?.();
      page.clearAllUserSelections(false, false);
    }

    const p = new URLSearchParams(window.location.search);
    ['campusId', 'campus'].forEach(key => p.delete(key));
    p.delete('cmrp_flow');
    const qs = p.toString();
    window.history.replaceState(
      {},
      '',
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname
    );
    page?.components?.urlManager?.parseURLParameters?.();

    void window.refreshProductsDisplay?.();

    window.courseMaterialsPageInstance?.hideErrorMessage?.();
    this.hideStudentIdError();
  }

  /**
   * Set the current mode (course or studentid)
   * @param {string} mode - 'course' or 'studentid'
   * @param {{ skipFlowReset?: boolean }} [options] - Set by URL/init so we do not wipe restored state.
   */
  setMode(mode, options = {}) {
    if (this.currentMode === mode) return;

    const { skipFlowReset = false } = options;
    if (!skipFlowReset) {
      this.resetPriorFlowStateOnMobileTabSwitch();
    }

    this.currentMode = mode;
    cmrpPersistFlowForMobile(mode === 'studentid' ? 'student-id' : 'course-builder');

    const toggleBtns = document.querySelectorAll('.mobile-mode-toggle__btn');
    toggleBtns.forEach(btn => {
      btn.classList.toggle('mobile-mode-toggle__btn--active', btn.dataset.mode === mode);
    });

    const courseSection = document.querySelector('.mobile-find-courses-section');
    const studentIdSection = document.getElementById('mobile-student-id-section');
    const selectedCoursesContainer = document.getElementById('mobile-selected-courses');

    if (mode === 'studentid') {
      const drawer = document.getElementById('mobile-drawer-section');
      drawer.classList.add('studentId-Flow-Enabled-Toggle');
      if (courseSection) courseSection.style.display = 'none';
      if (studentIdSection) studentIdSection.style.display = 'block';
      if (selectedCoursesContainer) {
        selectedCoursesContainer.style.display = 'none';
      }

      window.courseMaterialsSidebar?.enableStudentIdMode?.();

      this.updateSubmitButtonText('SUBMIT');
      this.updateMobileSubmitButtonStateForStudentId();
    } else {
      const drawer = document.getElementById('mobile-drawer-section');
      drawer.classList.remove('studentId-Flow-Enabled-Toggle');
      if (courseSection) courseSection.style.display = 'block';
      if (studentIdSection) studentIdSection.style.display = 'none';

      // Show manual course fields again; keep student ID drafts until a flow actually submits
      window.courseMaterialsSidebar?.disableStudentIdMode?.(true);

      this.hideStudentIdError();
      window.courseMaterialsPageInstance?.hideErrorMessage?.();

      if (selectedCoursesContainer) {
        const hasTags = selectedCoursesContainer.querySelector('.mobile-course-tag');
        selectedCoursesContainer.style.display = hasTags ? 'flex' : 'none';
      }

      this.updateSubmitButtonText('SUBMIT');
      this.updateMobileSubmitButtonState();
    }

    window.courseMaterialsSidebar?.syncMobileDrawerCampusTermSelects?.();
    window.courseMaterialsSidebar?.syncMobileCampusTermDisplays?.();

    const drawer = document.getElementById('mobile-drawer-section');
    if (drawer?.classList.contains('mobile-drawer-open')) {
      window.courseMaterialsSidebar?.checkMobileDrawerEmptyState?.();
    }
  }

  /**
   * Validate student ID input
   * @param {string} studentId - The student ID to validate
   * @returns {Object} - { valid: boolean, message: string }
   */
  validateStudentId(studentId) {
    const trimmed = String(studentId ?? '').trim();
    if (trimmed.length === 0) {
      return { valid: false, message: `${cmrpStudentIdLabel()} is required.` };
    }
    return { valid: true, message: '' };
  }

  /**
   * Update mobile submit button state for Student ID mode
   */
  updateMobileSubmitButtonStateForStudentId() {
    const submitButton = document.getElementById('mobile-submit-student-id');
    const campusSelect = getCanonicalCampusSelect();
    const termSelect = getCanonicalTermSelect();
    const studentIdInput = document.getElementById('mobile-student-id-input');

    if (!submitButton) return;

    const hasCampus = campusSelect && campusSelect.value;
    const hasTerm = termSelect && termSelect.value;
    const hasStudentId =
      studentIdInput && studentIdInput.value.trim().length > 0;

    if (hasCampus && hasTerm && hasStudentId) {
      submitButton.disabled = false;
      submitButton.classList.add('active');
    } else {
      submitButton.disabled = true;
      submitButton.classList.remove('active');
    }
  }

  /**
   * Handle student ID submission
   */
  handleStudentIdSubmit() {
    const campusSelect = getCanonicalCampusSelect();
    const termSelect = getCanonicalTermSelect();
    const studentIdInput = document.getElementById('mobile-student-id-input');

    if (!campusSelect || !termSelect || !studentIdInput) return;

    const studentId = studentIdInput.value.trim();

    // Validate
    const validation = this.validateStudentId(studentId);
    if (!validation.valid) {
      if (
        window.courseMaterialsPageInstance &&
        typeof window.courseMaterialsPageInstance.showErrorMessage === 'function'
      ) {
        window.courseMaterialsPageInstance.showErrorMessage(
          `Invalid ${cmrpStudentIdLabel()}: ${validation.message}`
        );
      } else {
        this.showStudentIdError(validation.message);
      }
      return;
    }

    // Hide any previous errors
    this.hideStudentIdError();
    window.courseMaterialsPageInstance?.hideErrorMessage?.();

    // Show loading state
    this.toggleStudentIdLoading(true);

    // Prepare student data
    const studentData = {
      selectedCampus: campusSelect.value,
      selectedTerm: termSelect.value,
      studentId: studentId
    };

    // Dispatch event to be handled by course-materials-page.js
    document.dispatchEvent(
      new CustomEvent('mobile:studentIdSubmitted', {
        detail: {
          studentData,
          onComplete: () => {
            this.toggleStudentIdLoading(false);
            window.courseMaterialsSidebar?.closeMobileDrawer();
          },
          onError: () => {
            this.toggleStudentIdLoading(false);
          }
        }
      })
    );
  }

  /**
   * Toggle loading state for student ID submission
   * @param {boolean} isLoading
   */
  toggleStudentIdLoading(isLoading) {
    const submitButton = document.getElementById('mobile-submit-student-id');
    const btnText = submitButton?.querySelector('.mobile-submit-btn-text');
    const btnSpinner = submitButton?.querySelector('.mobile-submit-btn-spinner');

    if (!submitButton) return;

    if (isLoading) {
      submitButton.disabled = true;
      if (btnText) btnText.textContent = 'LOADING...';
      if (btnSpinner) btnSpinner.style.display = 'inline-block';
    } else {
      if (btnText) btnText.textContent = 'FIND COURSES';
      if (btnSpinner) btnSpinner.style.display = 'none';
      this.updateMobileSubmitButtonStateForStudentId();
    }
  }

  /**
   * Show student ID error message
   * @param {string} message
   */
  showStudentIdError(message) {
    const errorContainer = document.getElementById('mobile-student-id-error');
    const errorText = errorContainer?.querySelector('.mobile-student-id-error__text');

    if (errorContainer && errorText) {
      errorText.textContent = message;
      errorContainer.style.display = 'block';
    }
  }

  /**
   * Hide student ID error message
   */
  hideStudentIdError() {
    const errorContainer = document.getElementById('mobile-student-id-error');
    if (errorContainer) {
      errorContainer.style.display = 'none';
    }
  }

  /**
   * Update submit button text
   * @param {string} text
   */
  updateSubmitButtonText(text) {
    const btnText = document.querySelector('#mobile-submit-student-id .mobile-submit-btn-text');
    if (btnText) {
      btnText.textContent = text;
    }
  }

  // ==========================================================
  // END STUDENT ID MODE METHODS
  // ==========================================================

  // Load all courses (like desktop)
  loadAllCourses() {
    if (window.courseMaterialsSidebar) {
      window.courseMaterialsSidebar.loadAllCourses();
    }
  }

  // Update right panel content
  updateRightPanelContent() {
    // Trigger the same event that desktop uses to update right panel
    document.dispatchEvent(new CustomEvent('sidebar:courseAdded', {
      detail: { 
        updateRightPanel: true,
        campus_entity_id: window.courseMaterialsSidebar?.campus_entity_id,
        term_id: window.courseMaterialsSidebar?.term_id
      }
    }));
  }

  // Handle mobile submit
  handleMobileSubmit() {
    // If in student ID mode, handle student ID submission
    if (this.currentMode === 'studentid') {
      this.handleStudentIdSubmit();
      return;
    }

    const selectedCourses = document.querySelectorAll('.mobile-course-tag');

    if (selectedCourses.length === 0) {
      return;
    }

    // New course-flow submission: drop prior Student ID / course results and selections
    window.courseMaterialsSidebar?.disableStudentIdMode?.();
    window.courseMaterialsSidebar?.clearAllUserSelections(false, false);
    window.courseMaterialsPageInstance?.components?.selectionManager?.clearSelections?.();

    // Ensure campus_entity_id and term_id are set
    const campusSelect = getCanonicalCampusSelect();
    const termSelect = getCanonicalTermSelect();
    
    if (campusSelect && campusSelect.value) {
      this.setCampusEntityId(campusSelect.value);
    }
    
    if (termSelect && termSelect.value) {
      const selectedTerm = this.config.ddcs_term.find(term => 
        term.name === termSelect.value
      );
      if (selectedTerm && window.courseMaterialsSidebar) {
        window.courseMaterialsSidebar.term_id = selectedTerm.term_id;
      }
    }
    
    // Register all selected courses
    selectedCourses.forEach(tag => {
      const courseData = {
        division: tag.getAttribute('data-division') || '',
        department: tag.getAttribute('data-department'),
        course: tag.getAttribute('data-course'),
        section: tag.getAttribute('data-section')
      };
      
      // Create course object with campus and term
      const objCourse = this.createCourseObject(courseData);
      
      // Ensure course object has required properties
      if (!objCourse.selectedCampus) {
        const campusSelect = getCanonicalCampusSelect();
        objCourse.selectedCampus = campusSelect?.value || '';
      }
      if (!objCourse.selectedTerm) {
        const termSelect = getCanonicalTermSelect();
        objCourse.selectedTerm = termSelect?.value || '';
      }
      
      // Register with desktop sidebar (division is part of course identity — FM-5756)
      if (window.courseMaterialsSidebar) {
        window.courseMaterialsSidebar.addSelectedCourse(
          courseData.department,
          courseData.course,
          courseData.section,
          courseData.section,
          courseData.division || '',
        );
      }
      
      // Batch with suppressRefresh so we run a single refresh after all filters update
      // (avoids overlapping refreshProductsDisplay runs that duplicate materials on mobile).
      document.dispatchEvent(
        new CustomEvent('sidebar:courseAdded', {
          detail: { course: objCourse, suppressRefresh: true },
        })
      );
    });

    if (selectedCourses.length > 0) {
      void window.refreshProductsDisplay?.();
    }
    
    // Hide initial placeholder state (courses are now registered)
    this.hideInitialPlaceholder();
    
    // Update right panel content
    this.updateRightPanelContent();
    
    // Close mobile drawer after submission
    this.closeMobileDrawer();
  }

}

// Export for global use
window.CourseMaterialsMobile = CourseMaterialsMobile;
